import { currentLanguage } from '@/i18n/format';
/**
 * Access to GeoServer from the browser.
 *
 * GeoServer is not published on the internet. The map reaches it only through
 * the backend proxy at `/api/geoserver`, which requires a logged-in user.
 *
 * Map images and legends are loaded by OpenLayers / <img> as plain image
 * requests, which cannot carry an Authorization header. For those the backend
 * also accepts the same JWT from a cookie that is only sent to
 * `/api/geoserver` (Path) and never to other sites (SameSite=Strict).
 */

/** Prefix of every GeoServer URL the app uses. */
export const GEOSERVER_PROXY_BASE = '/api/geoserver';

/** Must match JWTFilter.GEOSERVER_TOKEN_COOKIE in the backend. */
const TOKEN_COOKIE = 'gx_geoserver_token';

/** WMS/WFS endpoint of a workspace, e.g. `/api/geoserver/cite/wms`. */
export function geoserverEndpoint(workspace: string = 'cite', endpoint: 'wms' | 'wfs' | 'ows' = 'wms'): string {
  return `${GEOSERVER_PROXY_BASE}/${encodeURIComponent(workspace)}/${endpoint}`;
}

/** Legend image URL for one layer (WMS GetLegendGraphic through the proxy). */
export function geoserverLegendUrl(workspace: string, layerName: string): string {
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.1.1',
    REQUEST: 'GetLegendGraphic',
    LAYER: `${workspace}:${layerName}`,
    FORMAT: 'image/png',
    SCALE: '10000',
    // The generated styles carry their titles in en, nl, de and tr (GeoServer's LANGUAGE parameter).
    LANGUAGE: currentLanguage(),
  });
  return `${geoserverEndpoint(workspace, 'wms')}?${params.toString()}`;
}

/** Store the session token for GeoServer image requests. */
export function setGeoServerTokenCookie(token: string, expiresAtMs?: number | null): void {
  if (typeof document === 'undefined' || !token) return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const maxAge = expiresAtMs ? Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000)) : null;
  const age = maxAge !== null ? `; Max-Age=${maxAge}` : '';
  document.cookie =
    `${TOKEN_COOKIE}=${encodeURIComponent(token)}; Path=${GEOSERVER_PROXY_BASE}; SameSite=Strict${secure}${age}`;
}

/** Remove the GeoServer token cookie (logout, expired session). */
export function clearGeoServerTokenCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${TOKEN_COOKIE}=; Path=${GEOSERVER_PROXY_BASE}; SameSite=Strict; Max-Age=0`;
}

/** Re-create the cookie from the stored session (e.g. after a page reload). */
export function syncGeoServerTokenCookie(): void {
  if (typeof localStorage === 'undefined') return;
  const token = localStorage.getItem('auth_token');
  if (!token) {
    clearGeoServerTokenCookie();
    return;
  }
  const expires = Number(localStorage.getItem('token_expires_at')) || null;
  setGeoServerTokenCookie(token, expires);
}
